// League-only Hall of Fame / Hall of Shame.
//
// Fame and Shame both rank *instances* (a manager's week, a manager's
// captain call) drawn from a pool of members-times-weeks, so "best 3" and
// "worst 3" are genuinely different slices with room between them. Decision
// Accuracy and Captain Accuracy used to live here as their own always-visible
// ranked list, but each is one running percentage per manager, not an
// instance ranked out of members-times-weeks, so they now live as sortable
// columns on the standings table above (MyLeague.js) instead.
//
// `embedded` renders the same content without the page-level hero and outer
// max-width/padding, for a caller (MyLeague, directly below the standings
// table) that already supplies its own page chrome.
//
// Site-wide records deliberately live elsewhere (the Home tab), not here:
// this page is one league's own trophy case, and a cross-league record
// nested inside it would read as though it belonged to this league when it
// doesn't.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLeague } from "../context/LeagueContext";
import { Trophy, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { useLeagueRecords } from "../hooks/useLeagueRecords";
import { useTeams } from "../hooks/useTeams";
import { STAT_TEXT_STYLE } from "../utils/statText";
import UserAvatar from "../components/league/UserAvatar";
import ShareButton from "../components/ShareButton";
import { buildRecordShareText, buildHallShareText } from "../utils/hallShare";

function subtitleFor(row, kind, allTeams) {
  const parts = [row.firstName];
  // A season total has no one team or week to name, so it says how many
  // captained weeks it adds up.
  if (kind === "captainSeason") {
    parts.push(`${row.weeks} ${row.weeks === 1 ? "week" : "weeks"}`);
    return parts.filter(Boolean).join(" · ");
  }
  if (kind === "captain" || kind === "tripPlay" || kind === "freeze") {
    const team = allTeams?.[row.teamSlug];
    if (team?.school) parts.push(team.school);
  }
  if (row.week != null) parts.push(`Week ${row.week}`);
  return parts.filter(Boolean).join(" · ");
}

function RecordList({ rows, kind, valueColor, allTeams, emptyMessage }) {
  const { leagueId } = useLeague();
  if (rows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-sm" style={{ color: "var(--color-text-muted)" }}>
        {emptyMessage}
      </div>
    );
  }
  // Perfect Weeks lists instances, not a ranking: there's no "value" to show
  // and no order to number, so both give way to a plain achieved marker.
  const isInstanceList = kind === "perfectWeek";
  return (
    <div>
      {rows.map((row, i) => (
        // Every record opens the manager's card on the Scoreboard, through
        // the deep link the Home standings already use. A record from one
        // week (Best Week, 5x Play, Perfect Weeks) opens that week; a season
        // total (Captain Whisperer, bench) has no single week and opens the
        // current one.
        <Link
          key={`${row.memberId}-${row.week ?? "season"}`}
          to={`/${leagueId}/scoreboard?member=${row.memberId}${row.week != null ? `&week=${row.week}` : ""}`}
          className="flex items-center gap-3 px-4 py-2.5 no-underline"
          style={{ backgroundColor: i % 2 === 1 ? "var(--color-bg-row-alt)" : "var(--color-bg-card)", borderTop: i === 0 ? undefined : "1px solid var(--color-border)" }}
        >
          {!isInstanceList && (
            // Same size, weight and color as the standings table's own rank
            // digit (MyLeague.js), not a smaller/greyer echo of it.
            <span className="text-sm font-bold text-center flex-shrink-0" style={{ width: 16, color: "var(--color-text-strong)" }}>{i + 1}</span>
          )}
          <UserAvatar member={row} size={32} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate" style={{ color: "var(--color-text-strong)" }}>{row.teamName}</div>
            <div className="text-xs truncate" style={{ color: "var(--color-text-muted)" }}>{subtitleFor(row, kind, allTeams)}</div>
          </div>
          {isInstanceList ? (
            <Sparkles size={16} className="flex-shrink-0" style={{ color: valueColor }} />
          ) : (
            // Same size and weight as every other number on the League tab
            // (the standings table's Total/Wk/Dec%/Capt%): only the color
            // changes here, carrying which half of the record book this is.
            // A freeze is a difference, so it carries its sign: +24 saved,
            // -12 cost. Everything else is a score and reads as one.
            <span className="flex-shrink-0" style={{ ...STAT_TEXT_STYLE, color: valueColor }}>{kind === "freeze" && row.value > 0 ? `+${row.value}` : row.value}</span>
          )}
        </Link>
      ))}
    </div>
  );
}

// moreRows: the entries past the ones always shown, for a list that can open
// out (Best Week and Worst Week, to ten). The toggle closes the card rather
// than sitting above it, so it is found where the short list runs out.
//
// half and leagueName are for the share on the title line, which sends this
// one record as text: whatever the card is showing, so ten once opened out.
function CategoryCard({ label, caption, rows, moreRows = [], kind, valueColor, allTeams, emptyMessage, half, leagueName }) {
  const [expanded, setExpanded] = useState(false);
  const total = rows.length + moreRows.length;
  const visibleRows = expanded ? [...rows, ...moreRows] : rows;

  return (
    <>
      {/* The title line has the room: nothing else sits to the right of a
          record's name. The negative margin lets the 44px target overhang
          the line instead of spacing the title away from its caption. */}
      <div className="flex items-center justify-between px-1">
        <div className="text-sm font-bold" style={{ color: "var(--color-text-strong)" }}>{label}</div>
        {rows.length > 0 && (
          <div style={{ margin: "-14px -12px -14px 0" }}>
            <ShareButton
              getText={() => buildRecordShareText({ leagueName, half, label, kind, rows: visibleRows, allTeams })}
              label={`Share ${label}`}
              size={16}
            />
          </div>
        )}
      </div>
      {caption && (
        <p className="text-xs mb-2 px-1" style={{ color: "var(--color-text-muted)" }}>{caption}</p>
      )}
      <div className={`bg-surface-card rounded-2xl border border-hairline shadow-sm overflow-hidden mb-6 ${caption ? "" : "mt-2"}`}>
        <RecordList rows={visibleRows} kind={kind} valueColor={valueColor} allTeams={allTeams} emptyMessage={emptyMessage} />
        {moreRows.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="w-full flex items-center justify-center gap-1 text-[13px] font-semibold"
            style={{ minHeight: 44, color: "var(--color-brand)", borderTop: "1px solid var(--color-border)", background: "none" }}
          >
            {expanded ? `Show top ${rows.length}` : `Show top ${total}`}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>
    </>
  );
}

function HallOfFame({ embedded = false }) {
  const { fame, shame, loading } = useLeagueRecords();
  const { leagueData } = useLeague();
  const leagueName = leagueData?.name || "League";
  const { teams: allTeams } = useTeams();
  const [half, setHalf] = useState("fame");

  const categories = half === "fame"
    ? [
        { key: "week", label: "Best Week", caption: "The highest single-week scores in the league.", kind: "week", rows: fame.week, moreRows: fame.weekMore, valueColor: "var(--color-brand)", emptyMessage: "No week has been scored yet." },
        { key: "captain", label: "Captain Whisperer", caption: "The most captain points across the season, not counting 5x Play weeks.", kind: "captainSeason", rows: fame.captain, valueColor: "var(--color-success-text)", emptyMessage: "No captain points recorded yet." },
        { key: "tripPlay", label: "Best 5x Play", caption: "The biggest scores from a 5x Play.", kind: "tripPlay", rows: fame.tripPlay, valueColor: "var(--color-success-text)", emptyMessage: "Nobody's played their 5x yet." },
        { key: "freeze", label: "Best Freeze", caption: "The most points a freeze saved, against what the team finished on without it.", kind: "freeze", rows: fame.freeze, valueColor: "var(--color-success-text)", emptyMessage: "No freeze has saved points yet." },
        { key: "perfectWeek", label: "Perfect Weeks", caption: "No benched team outscored a starter, and the captain was the best of the lineup.", kind: "perfectWeek", rows: fame.perfectWeek, valueColor: "var(--color-captain-deep)", emptyMessage: "No perfect weeks yet." },
      ]
    : [
        { key: "week", label: "Worst Week", caption: "The lowest single-week scores in the league.", kind: "week", rows: shame.week, moreRows: shame.weekMore, valueColor: "var(--color-danger-text)", emptyMessage: "No week has been scored yet." },
        { key: "bench", label: "Most Left on the Bench", caption: "The most points scored by benched teams across the season.", kind: "bench", rows: shame.bench, valueColor: "var(--color-danger-text)", emptyMessage: "No bench points to compare yet." },
        { key: "captain", label: "Captain Calamity", caption: "The fewest captain points across the season, not counting 5x Play weeks.", kind: "captainSeason", rows: shame.captain, valueColor: "var(--color-danger-text)", emptyMessage: "No captain points recorded yet." },
        { key: "tripPlay", label: "Worst 5x Play", caption: "The lowest scores from a 5x Play.", kind: "tripPlay", rows: shame.tripPlay, valueColor: "var(--color-danger-text)", emptyMessage: "Nobody's played their 5x yet." },
        { key: "freeze", label: "Worst Freeze", caption: "The most points a freeze cost, against what the team finished on without it.", kind: "freeze", rows: shame.freeze, valueColor: "var(--color-danger-text)", emptyMessage: "No freeze has cost points yet." },
      ];

  return (
    <div className={embedded ? "mt-2" : "max-w-2xl mx-auto px-4 sm:px-6 py-4 pb-24"}>
      {/* The League tab's tour points at the title, the note and the toggle,
          not the records under them, which run several screens long. */}
      <div data-tour="league-hof">
      {embedded ? (
        <div className="flex items-center gap-2 mb-4 px-1">
          <Trophy size={16} style={{ color: "var(--color-captain-deep)" }} />
          <span className="text-sm font-bold uppercase tracking-wide" style={{ color: "var(--color-text-strong)" }}>Hall of Fame</span>
        </div>
      ) : (
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "var(--color-captain-fill)" }}>
            <Trophy size={24} style={{ color: "var(--color-captain-deep)" }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: "var(--color-text-strong)" }}>
            Hall of Fame
          </h1>
          <p style={{ color: "var(--color-text-muted)" }}>
            The best and worst this league has ever done.
          </p>
        </div>
      )}

      {/* Said once, above both halves, because it is true of every record in
          them (useLeagueRecords counts finished weeks only). Without it a
          manager who has just had a big Saturday looks for it here and takes
          its absence for a fault. */}
      <p className="text-xs mb-3 px-1" style={{ color: "var(--color-text-muted)" }}>
        Updated once a week. Records count finished weeks only, so this week's scores are added when the week closes on Sunday morning.
      </p>

      {/* Half and half, clickable: same segmented-control pattern as the
          Standings/Playoffs toggle on the League tab. */}
      {/* The share beside the toggle sends the half that is showing, as a
          digest: each record and who holds it. Each card below shares its
          own record in full. */}
      <div className="flex items-center gap-1 mb-6">
      <div className="flex-1 flex rounded-full p-1" style={{ backgroundColor: "var(--color-border)" }} role="tablist">
        {[
          { key: "fame", label: "Hall of Fame" },
          { key: "shame", label: "Hall of Shame" },
        ].map(({ key, label }) => {
          const active = half === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => setHalf(key)}
              className="flex-1 rounded-full py-2 text-[13px] font-semibold transition-colors"
              style={{
                backgroundColor: active ? "var(--color-bg-card)" : "transparent",
                color: active ? "var(--color-text-strong)" : "var(--color-text-muted)",
                boxShadow: active ? "0 1px 2px rgba(0,0,0,0.12)" : "none",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      {!loading && (
        <div style={{ margin: "-4px -12px -4px 0" }}>
          <ShareButton
            getText={() => buildHallShareText({ leagueName, half, categories, allTeams })}
            label={half === "fame" ? "Share the Hall of Fame" : "Share the Hall of Shame"}
          />
        </div>
      )}
      </div>
      </div>

      {loading ? (
        <div className="bg-surface-card rounded-2xl border border-hairline shadow-sm p-8 text-center mb-6">
          <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>Loading records...</p>
        </div>
      ) : (
        categories.map((cat) => (
          // Keyed by half as well: both halves have a "week" card, and one
          // opened out in Fame should not arrive already open in Shame.
          <CategoryCard
            key={`${half}-${cat.key}`}
            label={cat.label}
            caption={cat.caption}
            rows={cat.rows}
            moreRows={cat.moreRows}
            half={half}
            leagueName={leagueName}
            kind={cat.kind}
            valueColor={cat.valueColor}
            allTeams={allTeams}
            emptyMessage={cat.emptyMessage}
          />
        ))
      )}
    </div>
  );
}

export default HallOfFame;
